import clsx from "clsx";
import {
  ChangeEventHandler,
  PropsWithChildren,
  ReactElement,
  forwardRef,
} from "react";

export interface RadioCardProps {
  name?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: ChangeEventHandler;
  type: "checkbox" | "radio";
  className?: string;
  value: string;
  icon?: ReactElement;
  size?: "sm" | "md";
  disabled?: boolean;
}

const RadioCard = forwardRef<
  HTMLInputElement,
  PropsWithChildren<RadioCardProps>
>(function RadioCard(
  {
    name,
    value,
    size = "md",
    icon,
    defaultChecked,
    checked,
    onChange,
    className,
    children,
    disabled,
  },
  ref,
) {
  return (
    <label
      data-testid={`${name}-${value}`.toLocaleLowerCase()}
      className={clsx(
        "flex w-full text-app-body-2 relative group/switch",
        className,
        {
          "cursor-pointer": !disabled,
          "cursor-not-allowed": disabled,
        },
      )}
    >
      <input
        disabled={disabled}
        onChange={onChange}
        name={name}
        value={value}
        ref={ref}
        type="radio"
        defaultChecked={defaultChecked}
        checked={checked}
        className="h-none w-none absolute overflow-hidden opacity-0 peer"
      />
      <span
        className={clsx(
          "w-2s h-2s border border-neutral-150 rounded-full peer-checked:hidden absolute top-1/2 -translate-y-1/2",
          {
            "right-3s": icon && size === "md",
            "left-3s": !icon && size === "md",
            "right-s": icon && size === "sm",
            "left-s": !icon && size === "sm",
            "group-hover/switch:border-primary-800 group-active/switch:border-primary-875":
              !disabled,
          },
        )}
      />
      <span
        className={clsx(
          "hidden w-2s h-2s border border-current rounded-full peer-checked:block absolute top-1/2 -translate-y-1/2",
          {
            "right-3s": icon && size === "md",
            "left-3s": !icon && size === "md",
            "right-s": icon && size === "sm",
            "left-s": !icon && size === "sm",
            "text-primary-800": !disabled,
            "text-neutral-150": disabled,
          },
        )}
      >
        <span className="block rounded-full w-full h-full bg-current border-[2px] border-neutral-0"></span>
      </span>
      <span
        className={clsx(
          "bg-neutral-0 w-full py-[11px] border border-neutral-50 rounded-s peer-focus-visible:ring-2 ring-primary-800 ring-offset-2  flex gap-s items-center",
          {
            "pr-xl pl-3s": icon && size === "md",
            "pl-xl pr-3s": !icon && size === "md",
            "pr-3m pl-2s": icon && size === "sm",
            "pl-3m pr-2s": !icon && size === "sm",
            "group-hover/switch:border-primary-800 group-active/switch:border-primary-875 peer-checked/switch:border-primary-800":
              !disabled,
            "bg-neutral-12": disabled,
          },
        )}
      >
        {icon && (
          <span
            className={clsx("flex-none", {
              "h-3s w-3s": size === "sm",
              "h-2m w-2m": size === "md",
            })}
          >
            {icon}
          </span>
        )}
        <span
          className={clsx("flex-1", {
            "text-body-1": size === "md",
            "text-app-body-1": size === "sm",
          })}
        >
          {children}
        </span>
      </span>
    </label>
  );
});

export default RadioCard;
